import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../hooks/UseLanguage";
import { Globe, ChevronDown } from "lucide-react";
import "./LanguageSwitcher.scss";

const LanguageSwitcher = () => {
  const { t, i18n } = useTranslation();
  useLanguage();

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const activeLanguage = (
    i18n.resolvedLanguage || i18n.language || "en"
  ).toLowerCase();
  const isEnglish = activeLanguage.startsWith("en");
  const currentLabel = useMemo(() => (isEnglish ? "EN" : "AR"), [isEnglish]);

  const handleSelect = (lang: "en" | "ar") => {
    i18n.changeLanguage(lang);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      if (!containerRef.current) {return;}
      if (!containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  return (
    <div className="language-switcher" ref={containerRef}>
      <button
        type="button"
        className={`language-switcher__trigger ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(prev => !prev)}
      >
        <span className="language-switcher__icon" aria-hidden="true">
          <Globe />
        </span>
        <span className="language-switcher__label">{currentLabel}</span>
        <span className="language-switcher__chevron" aria-hidden="true">
          <ChevronDown />
        </span>
      </button>

      {isOpen && (
        <div className="language-switcher__menu">
          {(["en", "ar"] as const).map(lang => {
            const isSelected = activeLanguage.startsWith(lang);
            return (
              <button
                key={lang}
                type="button"
                className={`language-switcher__option ${isSelected ? "selected" : ""}`}
                onClick={() => handleSelect(lang)}
              >
                <span className="language-switcher__check" aria-hidden="true">
                  {isSelected ? "✓" : ""}
                </span>
                <span className="language-switcher__option-text">
                  {lang === "en" ? t("languageSwitcher.english") : t("languageSwitcher.arabic")}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
